import { useCallback, useEffect, useState } from 'react'
import {
  observeFavouriteItem,
  toggleFavouriteItem,
} from '@domain/features/user-favourites/user-favourites-feature'
import type {
  UserFavourite,
  UserFavouriteType,
} from '@domain/models/user-favourite'
import { useAuthUser } from '@presentation/hooks/useAuthUser'

export function useUserFavourite(
  itemId: string | undefined,
  type: UserFavouriteType,
) {
  const { isAuthenticated, loading: authLoading } = useAuthUser()
  const [favourite, setFavourite] = useState<UserFavourite | null>(null)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setError(null)

    if (!itemId) {
      setFavourite(null)
      setLoading(false)
      return
    }

    if (authLoading) {
      setLoading(true)
      return
    }

    if (!isAuthenticated) {
      setFavourite(null)
      setLoading(false)
      return
    }

    setLoading(true)
    const unsubscribe = observeFavouriteItem(
      itemId,
      (nextFavourite) => {
        setFavourite(nextFavourite)
        setLoading(false)
      },
      (nextError) => {
        setError(nextError.message)
        setLoading(false)
      },
      type,
    )

    return unsubscribe
  }, [authLoading, isAuthenticated, itemId, type])

  const toggle = useCallback(async (onAuthRequired?: () => void) => {
    if (!itemId) {
      return
    }
    if (!isAuthenticated) {
      onAuthRequired?.()
      return
    }

    try {
      setError(null)
      setToggling(true)
      await toggleFavouriteItem(itemId, type)
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : 'Failed to update favourite item',
      )
    } finally {
      setToggling(false)
    }
  }, [isAuthenticated, itemId, type])

  return {
    favourite,
    isFavourited: Boolean(favourite),
    loading: loading || authLoading,
    toggling,
    error,
    toggle,
  }
}
